import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import isArray from 'lodash/isArray';
import values from 'lodash/values';
import isBoolean from 'lodash/isBoolean';
import lowerCase from 'lodash/lowerCase';
import get from 'lodash/get';
import { Subject } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CustomComponent, navigationHelper, ngHelper } from '@cakeiteasy/web-utils';

import { SearchCakesService } from '../../services/search-cakes.service';
import { IFilterModel } from '../../models/i-filter.model';
import { ActivatedRoute } from '@angular/router';
import { BakeriesApiService } from '../../../api-services/services/bakeries-api.service';
import { IFilterCategoryGroup } from '../../models/i-filter-category-group';
import { debounceTime } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'search-cakes-panel',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './search-cakes-panel.component.html',
  styleUrls: ['./search-cakes-panel.component.scss']
})
export class SearchCakesPanelComponent extends CustomComponent implements OnChanges {
  @Input() public filters: IFilterModel;
  @Input() public refName: string;
  @Input() public countryCode: string;
  @Input() public maxGroupsToShow: number = 6;
  @Input() public excludingFilters: string[] = [];

  @Output() onMultiSelectOpen = new EventEmitter<boolean>();
  @Output() onShowMobileFilters = new EventEmitter<boolean>();
  @Output() onLocationFound = new EventEmitter<{ name: string, postNumber?: string }>();
  @Output() onPostNumberChosen = new EventEmitter<string>();

  public model: IFilterModel;
  public multiSelectOpenState: any = {};
  public showMobileFilters = false;
  public showMobileDeliveryCheck = false;
  public filtersChanged$ = new Subject<any>();
  public isMultiSelectOpen: boolean;
  public selectedDropdownGroup: { label: string; value: number; checked: boolean; ref: IFilterCategoryGroup };

  public linkGroups: { label: string; value: number; checked: boolean; ref: IFilterCategoryGroup }[];
  public dropdownGroups: { label: string; value: number; checked: boolean; ref: IFilterCategoryGroup }[];

  public hiddenFilters: any = {};

  constructor(public searchCakes: SearchCakesService,
              public bakeryApi: BakeriesApiService,
              private _route: ActivatedRoute) {
    super();
    this._subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'filters', (filters) => {
      this.model = cloneDeep(filters);
    });

    ngHelper.ifChanged(changes, 'excludingFilters', (excludingFilters) => {
      this.hiddenFilters = this._getHiddenFilters(this.excludingFilters);
    });

    if (get(this.model, 'productGroups.length') && !this.hiddenFilters['productGroups']) {
      this._initDropdownGroups(this.model.productGroups);
    }
  }

  public getDashed(str: string): string {
    return navigationHelper.transformLocationName(str);
  }

  // public onPostNumberChosen(postNumber) {
  //   const lastFilter = this.searchCakes.criteriaChanged$.getValue();
  //   this.searchCakes.criteriaChanged$.next({
  //     ...lastFilter,
  //     postNumber
  //   });
  // }

  public mobileFiltersOpenChange(show) {
    this.showMobileFilters = show;
    this.onShowMobileFilters.emit(show);
  }

  public multiSelectOpenChange(selectType: string, isOpen: boolean) {
    this.multiSelectOpenState[selectType] = isOpen;
    this.isMultiSelectOpen = values(this.multiSelectOpenState)
      .some((i) => i);

    this.onMultiSelectOpen.emit(this.isMultiSelectOpen);
  }

  public onApply(model?: IFilterModel) {
    if (model) {
      this.model = cloneDeep(model);
    }

    this.searchCakes.criteriaChanged$.next(this.model);
  }

  public onReset() {
    this.searchCakes.resetFilters(this.model);
  }

  public onMotiveChange(hasMotive) {
    this.model.hasMotive = hasMotive;
    this.searchCakes.criteriaChanged$.next(this.model);
  }

  public get cityName() {
    return lowerCase(this._route.snapshot.params.refName);
  }

  public selectGroup(group: { label: string; value: number; checked: boolean; ref: IFilterCategoryGroup }) {
    this.selectedDropdownGroup = null;
    this._productGroupChanged(group);
  }

  public dropdownSelectCategory(group: { label: string; value: number; checked: boolean; ref: IFilterCategoryGroup }) {
    this.selectedDropdownGroup = group;
    this._productGroupChanged(group);
  }

  private _getHiddenFilters(excludingFilters: string[]) {
    const result = {};
    excludingFilters.forEach((filterName) => {
      result[filterName] = true;
    });

    return result;
  }

  private _productGroupChanged(group: { label: string; value: number; checked: boolean; ref: IFilterCategoryGroup }) {
    group.checked = !group.checked;

    this.model.productGroups.forEach((g) => {
      g.checked = g.value === group.value && group.checked;
    });

    this._initDropdownGroups(this.model.productGroups);
    this.searchCakes.criteriaChanged$.next(this.model);
  }

  private _initDropdownGroups(groups) {
    const maxGroupsToShow = this._getMaxGroupsToShow(this.model, this.maxGroupsToShow, this.excludingFilters);

    if (groups.length > (maxGroupsToShow + 1)) {
      this.linkGroups = groups.slice(0, maxGroupsToShow);
      this.dropdownGroups = groups.slice(maxGroupsToShow, groups.length);
    } else {
      this.linkGroups = groups;
      this.dropdownGroups = [];
    }

    if (this.dropdownGroups.length) {
      this.selectedDropdownGroup = this.dropdownGroups.find((g) => g.checked) || null;
    }
  }

  private _getMaxGroupsToShow(filters: IFilterModel, maxGroupsToShow: number, excluding = []) {
    if (!filters) {
      return maxGroupsToShow;
    }

    const cleanFilters = omit(filters, ['productGroups', 'hasMotive', 'categoryId', 'refName'].concat(excluding));

    const filtersCount = values(cleanFilters)
      .reduce<number>((res, filter) => {
        return isArray(filter) && filter.length || isBoolean(filter)
        || !isArray(filter) && !isBoolean(filter) && filter !== undefined
          ? res + 1 : res;
      }, 0);

    return maxGroupsToShow > filtersCount ? maxGroupsToShow - filtersCount : 0;
  }

  private _subscribe() {
    this.addSubscriber(this.searchCakes.criteriaChanged$.subscribe((model) => {
      if (!this.isMultiSelectOpen) {
        this.model = cloneDeep(model);
      }
    }));

    this.addSubscriber(this.filtersChanged$.pipe(debounceTime(500)).subscribe(() => {
      this.onApply();
    }));
  }
}
